import { useState } from "react";
import { Tag, Button, Loader, toast, type TagTone } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { Link } from "@tanstack/react-router";
import { logger } from "@bfc-software/app-toolbox";
import {
  useTaskPlannerFetchClient,
  type TaskPlannerFetchClient,
} from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";

import type { ActivationView, TaskPlanDetail } from "./types";
import styles from "./shared.module.css";

const STATUS_TONES: Record<TaskPlanDetail["status"], TagTone> = {
  completed: "success",
  processing: "info",
  queued: "neutral",
  failed: "danger",
};

export { STATUS_TONES };

async function downloadArtifact(
  client: TaskPlannerFetchClient,
  orgId: string,
  artifactId: string,
  kind: string,
) {
  const { data, error } = await client.GET(
    "/{orgId}/artifacts/{artifactId}/download",
    { params: { path: { orgId, artifactId } }, parseAs: "blob" },
  );
  if (error || !data) {
    logger.error(`Failed to download artifact ${artifactId}`);
    return;
  }
  const url = URL.createObjectURL(data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${kind}-${artifactId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function extractFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get("content-disposition");
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

async function requestLabel(
  client: TaskPlannerFetchClient,
  orgId: string,
  taskPlanId: string,
  warehouseExtId: string,
): Promise<boolean> {
  const { data, error, response } = await client.POST(
    "/{orgId}/task-plans/{taskPlanId}/request-label",
    {
      params: { path: { orgId, taskPlanId } },
      body: { warehouse_ext_id: warehouseExtId },
      parseAs: "blob",
    },
  );
  if (error || !data) {
    logger.error(`Failed to request label for task plan ${taskPlanId}`);
    return false;
  }
  const url = URL.createObjectURL(data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = extractFilename(response, `task-plan-${taskPlanId}-label.zpl`);
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

export function PlanSummary({
  plan,
  orgId,
  activation,
  lang,
  location,
  t,
}: {
  plan: TaskPlanDetail;
  lang: string;
  location: string;
  orgId: string;
  activation?: ActivationView;
  t: TFunction;
}) {
  const client = useTaskPlannerFetchClient();
  const routeList = plan.routes.map((route) => {
    return (
      <span key={`${plan.id}-${route.route_code}`} className={styles.routePair}>
        <span
          className={styles.planSummaryRouteText}
          data-test-role="plan-summary-route-code"
        >
          {route.route_code}
        </span>
        <span
          className={styles.planSummaryRouteText}
          data-test-role="plan-summary-route-date"
        >
          {route.route_date}
        </span>
      </span>
    );
  });

  const [isRequestingLabel, setIsRequestingLabel] = useState(false);

  const handleRequestLabel = () => {
    setIsRequestingLabel(true);
    requestLabel(client, orgId, plan.id, location)
      .then((ok) => {
        toast[ok ? "success" : "error"](
          t(
            ok
              ? "page.taskPlanDetail.summary.labelRequestSucceeded"
              : "page.taskPlanDetail.summary.labelRequestFailed",
          ),
        );
      })
      .finally(() => setIsRequestingLabel(false));
  };

  return (
    <div className={styles.planSummary} data-test-role="task-plan-summary">
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.status")}
        </span>
        <Tag
          label={plan.status}
          display={{ tone: STATUS_TONES[plan.status] }}
        />
      </div>
      {activation && (
        <div className={styles.summaryRow} data-test-role="activation-status">
          <span className={styles.summaryLabel}>
            {t("page.taskPlanDetail.summary.activationStatus")}
          </span>
          {activation.kind === "loading" ? (
            <Loader type="inline" />
          ) : activation.kind === "present" ? (
            <Tag
              label={activation.status}
              display={{ tone: STATUS_TONES[activation.status] }}
            />
          ) : (
            <span
              className={styles.assignmentMuted}
              data-test-role="activation-not-activated"
            >
              {t("page.taskPlanDetail.summary.notActivated")}
            </span>
          )}
        </div>
      )}
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.routeCode")}
        </span>
        {routeList}
      </div>
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.totalLines")}
        </span>
        <span>{plan.total_lines}</span>
      </div>
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.totalTasks")}
        </span>
        <span>{plan.total_tasks}</span>
      </div>
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.standardTasks")}
        </span>
        <span>{plan.standard_task_count}</span>
      </div>
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.bulkTasks")}
        </span>
        <span>{plan.bulk_task_count}</span>
      </div>
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.createdAt")}
        </span>
        <DateValue dateValue={plan.created_at} />
      </div>
      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.completedAt")}
        </span>
        <DateValue dateValue={plan.completed_at ?? null} />
      </div>

      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.label")}
        </span>
        <Button
          kind="text-icon-left"
          variant="secondary"
          scale="inline"
          text={
            isRequestingLabel
              ? t("page.taskPlanDetail.summary.labelFetching")
              : t("page.taskPlanDetail.summary.labelRequest")
          }
          icon="Download"
          isDisabled={isRequestingLabel}
          onPress={handleRequestLabel}
        />
      </div>
      {plan.artifacts.length > 0 && (
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>
            {t("page.taskPlanDetail.summary.artifacts")}
          </span>
          <div className={styles.artifactList}>
            {plan.artifacts.map((a) => (
              <Button
                key={a.artifact_id}
                kind="text-icon-left"
                variant="secondary"
                scale="inline"
                text={a.kind}
                icon="Download"
                onPress={() =>
                  downloadArtifact(client, orgId, a.artifact_id, a.kind)
                }
              />
            ))}
          </div>
        </div>
      )}

      <div className={styles.summaryRow}>
        <span className={styles.summaryLabel}>
          {t("page.taskPlanDetail.summary.simulation")}
        </span>
        <div className={styles.artifactList}>
          <Link
            key="plan-simulatioon"
            to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan/simulate"
            params={{ org: orgId, lang, "task-plan": plan.id, location }}
          >
            {t("page.taskPlanDetail.summary.simulate")}
          </Link>
        </div>
      </div>
    </div>
  );
}
